import { useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Mail, Lock, LogIn, ArrowRight, FileText } from 'lucide-react';
import { isTurnstileDisabled, verifyTurnstileToken } from '../lib/turnstile';

const PILLARS = [
  {
    title: 'Editor de blocos moderno',
    desc: 'Crie posts e páginas com a mesma fluidez de 2026.',
  },
  {
    title: 'Multisite nativo',
    desc: 'Uma instalação, múltiplos sites independentes.',
  },
  {
    title: 'LGPD por design',
    desc: 'RLS, audit log imutável e direitos do titular embarcados.',
  },
];

export function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preencha todos os campos.');
      return;
    }
    setLoading(true);
    try {
      await verifyTurnstileToken(turnstileToken, async (token) =>
        supabase.functions.invoke('verify-turnstile', { body: { token } }),
      );

      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      toast.success('Login realizado com sucesso!');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'E-mail ou senha inválidos.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-surface text-text flex">
      <aside className="relative hidden lg:flex flex-col justify-between p-12 xl:p-16 w-1/2 bg-card border-r border-border">
        <header className="flex items-center gap-3">
          <BrandMark />
          <div>
            <span className="font-display text-lg font-bold text-ink">Lumenise CMS</span>
            <span className="block text-xs text-muted font-mono uppercase tracking-wider">White-label · Multisite</span>
          </div>
        </header>

        <div className="space-y-8">
          <div className="space-y-4">
            <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight text-ink">
              Gerencie sites com clareza.
            </h1>
            <p className="text-base text-muted max-w-md">
              CMS moderno, multi-tenant e pronto para escalar. Crie conteúdo, organize mídia e publique em minutos.
            </p>
          </div>

          <ul className="space-y-4">
            {PILLARS.map((p, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="mt-1 h-2 w-2 rounded-full bg-accent" />
                <div>
                  <p className="text-sm font-semibold text-ink">{p.title}</p>
                  <p className="text-xs text-muted">{p.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <footer className="text-xs text-muted">
          © 2026 Lumenise ·{' '}
          <a href="/legal/privacidade" className="hover:text-accent">Privacidade</a> ·{' '}
          <a href="/legal/termos" className="hover:text-accent">Termos</a>
        </footer>
      </aside>

      <section className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-6">
          <div className="lg:hidden flex flex-col items-center gap-2 mb-6">
            <BrandMark />
            <span className="font-display text-xl font-bold text-ink">Lumenise CMS</span>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 shadow-md space-y-6">
            <div className="space-y-1">
              <h2 className="font-display text-2xl font-bold text-ink">Entrar</h2>
              <p className="text-sm text-muted">Acesse seu painel administrativo.</p>
            </div>

            <form onSubmit={handleEmailLogin} className="space-y-5">
              <PremiumField
                id="email"
                label="E-mail"
                type="email"
                icon={Mail}
                placeholder="seu@email.com"
                value={email}
                onChange={setEmail}
                required
              />

              <PremiumField
                id="password"
                label="Senha"
                type="password"
                icon={Lock}
                placeholder="••••••••"
                value={password}
                onChange={setPassword}
                required
              />

              {!isTurnstileDisabled && (
                <div className="flex justify-center">
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                    onSuccess={(token) => setTurnstileToken(token)}
                    options={{
                      theme: 'auto',
                      language: 'pt-br',
                      size: 'flexible',
                    }}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 rounded-xl py-3 px-4 text-sm font-semibold bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    <LogIn className="h-4 w-4" />
                    <span>Entrar na conta</span>
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-muted">
            <FileText className="inline h-3 w-3 mr-1" />
            Ao entrar, você concorda com os{' '}
            <a href="/legal/termos" className="text-accent hover:underline">Termos</a> e{' '}
            <a href="/legal/privacidade" className="text-accent hover:underline">Privacidade</a>.
          </p>
        </div>
      </section>
    </main>
  );
}

function BrandMark() {
  return (
    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center">
      <svg viewBox="0 0 24 24" className="h-5 w-5 text-ink" fill="currentColor">
        <path d="M12 2L4 7v10l8 5 8-5V7l-8-5zm0 2.5L17.5 8 12 11.5 6.5 8 12 4.5zM6 9.5l5 3v8.25l-5-3.125V9.5zm12 0v8.125l-5 3.125V12.5l5-3z" />
      </svg>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

interface PremiumFieldProps {
  id: string;
  label: string;
  type: string;
  icon: typeof Mail;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}

function PremiumField({ id, label, type, icon: Icon, placeholder, value, onChange, required }: PremiumFieldProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs font-semibold text-muted uppercase tracking-wider">
        {label}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-surface text-ink placeholder-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent text-sm"
        />
      </div>
    </div>
  );
}
